import React, { useEffect, useRef } from 'react'
import styled from '@emotion/styled'

import { LandingPageNumber, ContactPageNumber, VehiclePageNumber, CertificatePageNumber } from '../../constants'
import { fontFamilyOpenSans, white } from '../../constants/principles'
import useHeaderFooterData from '../../hooks/useHeaderFooterData'
import useGetElementHeight from '../../hooks/useGetElementHeight'
import { BrowserUtils } from '../../utils/siteUtils'
import DuplicateOfferModal from '../generic/DuplicateOfferModal'
import ExitstitialModal from '../generic/modals/ExitstitialModal'
import Survey from '../generic/Survey'
import VinModal from '../review/VinModal'
import OptionsModal from '../vehicleDetails/OptionsModal'
import AtcHeader from './AtcHeader'
import AtcFooter from './AtcFooter'
import EmptyHeader from './EmptyHeader'
import EmptyFooter from './EmptyFooter'
import ExitDiv from './ExitDiv'
import FunnelLayoutHelmet from './FunnelLayoutHelmet'
import HelpFooter from './HelpFooter'
import LoadingInfo from './LoadingInfo'
import KbbBrandingHeader from './KbbBrandingHeader'
import KbbHeader from './KbbHeader'
import KbbFooter from './KbbFooter'
import { ModalContainer } from './Modal'
import { fireCustomEvent } from '../../analytics/platforms/infoTrust'
import { getTheme } from '../../constants/themes'

const surveyAnswers = {
  slp: [
    { id: '1', text: "Didn't know my license plate or VIN" },
    { id: '2', text: 'Not what I expected' },
    { id: '3', text: 'Just wanted my Trade-in Value' },
    { id: '4', text: 'Not ready to sell/trade in my car yet' }
  ],
  detail: [
    { id: '1', text: "Didn't know how to answer some of the questions" },
    { id: '2', text: 'Too many questions overall' },
    { id: '3', text: 'Just wanted my Trade-in Value' },
    { id: '4', text: 'Not ready to sell/trade in my car yet' }
  ],
  contact: [
    { id: '1', text: "Didn't expect to give my personal info" },
    { id: '2', text: "Don't want dealers contacting me" },
    { id: '3', text: 'Just wanted my Trade-in Value' },
    { id: '4', text: 'Not ready to sell/trade in my car yet' }
  ]
}

export default ({
  children,
  location,
  loadingInfo,
  loadingProspectDocuments,
  year,
  make,
  model,
  experienceState,
  updateGlobalState,
  globalState,
  resetOptionSelections,
  urlState,
  saveToUrlState,
  getQuestionResponse,
  addImpressions,
  eligibility,
  isBuildStep,
  usingLandingWebComponent,
  offer,
  ...otherProps
}) => {
  let { kbbHeaderFooterData } = useHeaderFooterData()
  // Remove url if javascriptFunc is present in any item
  if (Array.isArray(kbbHeaderFooterData?.footerLinks)) {
    kbbHeaderFooterData.footerLinks = kbbHeaderFooterData.footerLinks.map(item => {
      if (!item || typeof item !== 'object') return item

      // Prioritize explicit id matches
      if (item.id === 'navFooterDontSellMyInfo') {
        // eslint-disable-next-line no-script-url
        return { ...item, url: 'javascript:caiprivacy.doNotSellOrShareMyPersonalInformation();' }
      }
      if (item.id === 'navFooterDontProcessMyInfo') {
        // eslint-disable-next-line no-script-url
        return { ...item, url: 'javascript:caiprivacy.limitTheUseOfMySensitivePersonalInformation();' }
      }

      // Fallback: if item has a javascriptFunc property, remove url so the JS handler can be used
      if (item.javascriptFunc) {
        const { url, ...rest } = item
        return rest
      }

      return item
    })
  }
  const [height] = useGetElementHeight('stickyDiv')
  const isDesktop = BrowserUtils.isDesktop()
  const headerType = experienceState.headerType || 'kbb' // to pregenerate
  const isAdmin = experienceState && experienceState.isAdmin
  const modalProps = { ...otherProps, updateGlobalState, urlState, globalState, isAdmin }
  const isSmartFrame = globalState && globalState.smartFrame
  const isIframe = BrowserUtils.isIframe() && !isSmartFrame && !isAdmin
  const modalShown = getQuestionResponse('x') === 1
  const runUntilDate = new Date('Jan 8 2020')
  const surveyKey = urlState.p === LandingPageNumber ? 'slp' : urlState.p === ContactPageNumber ? 'contact' : urlState.p < ContactPageNumber ? 'detail' : ''
  const showExitDiv =
    !modalShown &&
    !experienceState.isAdmin &&
    !experienceState.isAssigned &&
    (eligibility ? eligibility.isEligible : true) &&
    isDesktop &&
    (!runUntilDate || new Date() < runUntilDate) &&
    (!urlState.p || urlState.p === VehiclePageNumber || urlState.p === ContactPageNumber)
  const { initialQueryParams } = BrowserUtils.getInitParams()

  const shouldShowOfferCreationComponent = experienceState.shouldShowOfferCreationComponent
  const { theme } = experienceState || {}
  const { fontFamily, backgroundColor } = getTheme(theme) || {}

  useEffect(() => {
    function handleMessage(e) {
      if (e.data) {
        try {
          const details = e.data
          updateGlobalState({ frameScrollPosition: details.scrollPosition.y, viewportHeight: details.viewport.height, frameHeight: details.frameInfo.height, smartFrame: true })
        } catch {}
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [updateGlobalState, globalState])

  const headerRef = useRef()
  const footerRef = useRef()

  useEffect(() => {
    const headerClickEvent = e => {
      fireCustomEvent(e, true)
    }
    const footerClickEvent = e => {
      fireCustomEvent(e, false)
    }
    if (globalState.environment.funnelWebComponentUrl) {
      BrowserUtils.addScript(globalState.environment.funnelWebComponentUrl + '?' + Math.floor(Math.random() * 9999), document.head, 'module', false)
    }

    if (headerRef && headerRef.current) {
      const header_qaid_elms = headerRef.current.querySelectorAll('[data-qaid]')
      const header_a_without_qaid_elms = headerRef.current.querySelectorAll('a:not([data-qaid])')
      const footer_qaid_elms = footerRef.current.querySelectorAll('[data-qaid]')
      const footer_a_without_qaid_elms = footerRef.current.querySelectorAll('a:not([data-qaid])')

      header_qaid_elms.forEach(element => {
        if (element.getAttribute('data-qaid') === 'lnk-car-res-rvw') {
          element.addEventListener('mouseenter', headerClickEvent, false)
        } else {
          element.addEventListener('click', headerClickEvent, false)
        }
      })
      header_a_without_qaid_elms.forEach(element => {
        element.addEventListener('click', headerClickEvent, false)
      })

      footer_qaid_elms.forEach(element => {
        element.addEventListener('click', footerClickEvent, false)
      })
      footer_a_without_qaid_elms.forEach(element => {
        element.addEventListener('click', footerClickEvent, false)
      })

      return () => {
        header_qaid_elms.forEach(element => {
          element.removeEventListener('click', headerClickEvent, false)
        })
        header_a_without_qaid_elms.forEach(element => {
          element.removeEventListener('click', headerClickEvent, false)
        })

        footer_qaid_elms.forEach(element => {
          element.removeEventListener('click', footerClickEvent, false)
        })
        footer_a_without_qaid_elms.forEach(element => {
          element.removeEventListener('click', footerClickEvent, false)
        })
      }
    }
  }, [])

  return (
    <>
      <FunnelLayoutHelmet {...{ year, make, model, location, globalState, updateGlobalState, urlState, experienceState, ...otherProps }} />
      <MainDiv
        isIframe={isIframe && urlState.p !== CertificatePageNumber}
        paddingTop={'0'}
        isLanding={urlState && !urlState.p}
        isAdmin={experienceState.isAdmin}
        shouldShowOfferCreationComponent={shouldShowOfferCreationComponent}
        usingLandingWebComponent={usingLandingWebComponent}>
        <PrintSupress>
          {showExitDiv && <ExitDiv saveToUrlState={saveToUrlState} updateGlobalState={updateGlobalState} exitstential={globalState.exitstential} setShowModal={otherProps.setShowModal} />}
          {headerType === 'atc' ? (
            <div ref={headerRef}>
              <AtcHeader isDesktop={isDesktop} />
            </div>
          ) : headerType === 'kbb' ? (
            <KbbHeader links={kbbHeaderFooterData} isBuildStep={isBuildStep} brand={headerType} environment={globalState.environment} />
          ) : headerType === 'kbbBranding' ? (
            <KbbBrandingHeader />
          ) : headerType === 'noHeader' ? (
            ''
          ) : (
            <EmptyHeader />
          )}
        </PrintSupress>
        <StyledDiv id={'stickyDiv'} stickyHeight={height} isIframe={isIframe} isAdmin={isAdmin} fontFamily={fontFamily} backgroundColor={backgroundColor}>
          {children}
        </StyledDiv>
        {initialQueryParams.utm_source && initialQueryParams.utm_source === 'email' && otherProps.offer && otherProps.offer.offerCode && otherProps.offer.offerCode.toUpperCase() === 'TC' ? (
          <div></div>
        ) : (
          (headerType === 'atc' || headerType === 'kbb' || headerType === 'kbbBranding') &&
          !usingLandingWebComponent && <HelpFooter shouldSticky={isIframe} setShowModal={modalProps.setShowModal} dealerIsDirectToKBBv2={experienceState.dealerIsDirectToKBBv2} />
        )}

        <FooterDiv usingLandingWebComponent={usingLandingWebComponent}>
          <PrintSupress>
            {headerType === 'atc' ? (
              <div ref={footerRef}>
                <AtcFooter />
              </div>
            ) : headerType === 'kbb' ? (
              <KbbFooter links={kbbHeaderFooterData} isDesktop={isDesktop} />
            ) : headerType === 'kbbBranding' ? (
              ''
            ) : headerType === 'noHeader' ? (
              ''
            ) : (
              <EmptyFooter />
            )}
          </PrintSupress>
        </FooterDiv>

        {isIframe && <SpacingDiv />}
      </MainDiv>
      <LoadingInfo {...{ loadingInfo, loadingProspectDocuments, resetOptionSelections, urlState, experienceState }} />
      {!usingLandingWebComponent && <VinModal key={globalState.showModal ? 'vinModal1' : 'vinModal2'} {...modalProps} />}
      {!usingLandingWebComponent && <DuplicateOfferModal key={globalState.submitVinCount || 0} {...modalProps} />}
      <OptionsModal key={'optionsModal'} {...modalProps} />
      {isDesktop && (
        <ExitstitialModal>
          <Survey
            questionSubheader={"Please tell us why you're not completing your Offer."}
            answers={surveyAnswers[surveyKey] || []}
            isDesktop={isDesktop}
            updateGlobalState={updateGlobalState}
            showClose={true}
            onClickClose={() => otherProps.setShowModal(false)}
            addImpressions={addImpressions}
            surveyKey={`icoexit_${surveyKey}`}
          />
        </ExitstitialModal>
      )}
      {!usingLandingWebComponent && <ModalContainer isIframe={isIframe} />}
    </>
  )
}

const SpacingDiv = styled.div`
  flex-grow: 1;
`
const MainDiv = styled.div`
  width: 100%;
  min-height: ${p => (p.isAdmin ? '' : '100vh')};
  padding-top: ${p => p.paddingTop};
  display: flex;
  flex-direction: column;
  min-width: 320px;
  -webkit-text-size-adjust: 100%;
  background-color: ${p => (p.isLanding ? '#f6f6f6' : 'white')};
  ${p =>
    p.shouldShowOfferCreationComponent &&
    `header { position: relative; z-index: 4 }; transition-delay: 0.5s; transition-property: transition-property: z-index; }
     header.sticky-position-mobile-mode { transition-delay: 0s; transition-property: z-index; z-index: 4; }
    `}
  #globalHeader {
    position: relative;
    z-index: 4;
  }
`
const StyledDiv = styled.div`
  font-family: ${p => p.fontFamily || fontFamilyOpenSans};
  display: flex;
  ${p => !p.isIframe && `flex-grow: 1;`}
  flex-direction: column;
  background-color: ${p => p.backgroundColor || white};
`
const PrintSupress = styled.div`
  @media print {
    display: none;
  }
`

const FooterDiv = styled.div`
  ${p => (p.usingLandingWebComponent ? 'z-index: 4;' : '')}
`
